import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Formik, Form } from "formik";
import { Field } from "components/ui/Forms/Field";

import { Card, CardTitle, CardBody } from "components/ui/Card";
import { ButtonAsLink } from "components/ui/ButtonAsLink";

import { CheckboxInputGroup } from "components/ui/Forms/CheckboxInputGroup";
import { InputGroup } from "components/ui/Forms/InputGroup";
import { Checkbox } from "components/ui/Forms/Checkbox";
import VerticallySpaced from "components/ui/VerticallySpaced";
import { SmallSpacer } from "components/ui/VerticalSpacers";
import { RequiredIndicator } from "components/ui/Forms/RequiredIndicator";
import HairlineRule from "components/ui/HairlineRule";
import { useCSRFToken } from "components/hooks/useCSRFToken";
import { ModernBlueButton } from "components/ui/Buttons";

import { FormServerError } from "./FormServerError";
import { FootNote, FootNotes } from "./FootNotes";

export function DelegateWelcomeCardUI() {
  const csrfToken = useCSRFToken();
  const navigate = useNavigate();

  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchAndShowTerms = async () => {
    const res = await fetch(
      `/api/campus_solutions/delegate_terms_and_conditions`
    );

    if (res.ok) {
      const { feed: { termsAndConditions } = {} } = await res.json();
      setTermsAndConditions(termsAndConditions);
      setShowTerms(true);
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);

    const res = await fetch(`/api/campus_solutions/delegate_access`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": csrfToken,
      },
      body: JSON.stringify({
        securityKey: values.key,
        proxyEmailAddress: values.email,
        agreeToTerms: values.terms,
      }),
    });

    const data = await res.json();
    const { errored, feed: { errmsgtext } = {} } = data;

    setSubmitting(false);

    if (errored) {
      setErrorText(errmsgtext);
    } else {
      setIsSaving(true);
      navigate("/toolbox?refresh=true");
    }
  };

  return (
    <Card>
      <CardTitle>
        <h2>Linking Your Account</h2>
      </CardTitle>
      <CardBody>
        <Formik
          onSubmit={onSubmit}
          initialValues={{
            email: "",
            key: "",
            terms: false,
          }}
          validate={validate}
          validateOnMount={true}
        >
          {({ isSubmitting, isValid }) => (
            <Form>
              <FormServerError errorText={errorText} />
              <h3>Link Your Account with Your Student</h3>
              <div>Enter information from the email message you received:</div>
              <SmallSpacer />
              <VerticallySpaced $spacing="medium-spacing">
                <InputGroup>
                  <label htmlFor="key">
                    Security Key: <RequiredIndicator />
                  </label>
                  <Field
                    type="text"
                    name="key"
                    maxLength="20"
                    required="required"
                  />
                </InputGroup>

                <InputGroup>
                  <label htmlFor="emailAddress">
                    Email Address [1]: <RequiredIndicator />
                  </label>
                  <Field
                    type="email"
                    name="email"
                    maxLength="254"
                    required="required"
                  />
                </InputGroup>

                <CheckboxInputGroup>
                  <Checkbox id="terms" name="terms" type="checkbox" />
                  <label htmlFor="terms">
                    I agree to the Terms and Conditions.
                  </label>
                  {!showTerms && (
                    <ButtonAsLink onClick={fetchAndShowTerms}>
                      Show
                    </ButtonAsLink>
                  )}
                </CheckboxInputGroup>

                {showTerms && (
                  <div style={{ background: `var(--gallery)` }}>
                    {termsAndConditions}
                  </div>
                )}

                <div>
                  <ModernBlueButton
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    {isSaving ? <>Linking&hellip;</> : "Link"}
                  </ModernBlueButton>
                </div>

                <HairlineRule style={{ margin: 0 }} />

                <FootNotes>
                  <FootNote>
                    <dt>[1]</dt>
                    <dd>
                      Email address where you received your delegate message.
                    </dd>
                  </FootNote>
                </FootNotes>
              </VerticallySpaced>
            </Form>
          )}
        </Formik>
      </CardBody>
    </Card>
  );
}

function validate(values) {
  const errors = {};

  if (!values.email) {
    errors.email = "Required";
  }

  if (!values.key) {
    errors.key = "Required";
  }

  if (!values.terms) {
    errors.terms = "Required";
  }

  return errors;
}
